import { FileText, Download, ExternalLink } from 'lucide-react'
import CTASection from '../components/CTASection'
import SectionHeader from '../components/SectionHeader'
import { ENROLLMENT, SITE } from '../content/siteContent'

export default function EnrollmentPage() {
  return (
    <main>
      {/* Page hero */}
      <section className="bg-gradient-to-br from-brand-navy to-brand-blue pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} aria-hidden="true" />
        <div className="section-container relative z-10 text-center">
          <p className="text-brand-amber text-xs font-round font-bold tracking-[0.15em] uppercase mb-3">Enrollment</p>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">{ENROLLMENT.headline}</h1>
          <p className="text-slate-200 text-lg max-w-xl mx-auto">{ENROLLMENT.subheadline}</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
            <path d="M0 60V30C240 0 480 50 720 30C960 10 1200 50 1440 30V60H0Z" fill="#FDF8F0" />
          </svg>
        </div>
      </section>

      {/* Steps */}
      <section className="section-pad bg-brand-ivory">
        <div className="section-container">
          <SectionHeader eyebrow="Getting Started" headline="Your Path to Enrollment" subheadline="We've made it simple and welcoming. Here's how to get started." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ENROLLMENT.steps.map((step) => (
              <div key={step.step} className="relative card text-center hover:shadow-lift hover:-translate-y-1 transition-all duration-300">
                {/* Step number */}
                <div className="w-12 h-12 rounded-full bg-brand-navy text-white font-display font-bold text-xl flex items-center justify-center mx-auto mb-4">
                  {step.step}
                </div>
                {/* Connector line (desktop) */}
                {parseInt(step.step) < ENROLLMENT.steps.length && (
                  <div className="hidden lg:block absolute top-10 left-[calc(100%-0px)] w-6 h-0.5 bg-brand-sky/30" aria-hidden="true" />
                )}
                <h3 className="text-brand-navy font-display font-bold text-lg mb-2">{step.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="section-pad bg-white">
        <div className="section-container max-w-3xl">
          <SectionHeader eyebrow="2025–2026 Documents" headline="Enrollment Documents" subheadline="Download the forms you need to get started." />
          <ul className="space-y-3" role="list">
            {ENROLLMENT.documents.map((doc) => (
              <li key={doc.label}>
                <a
                  href={doc.href}
                  className="flex items-center gap-4 card hover:shadow-lift hover:-translate-y-0.5 transition-all duration-200 group"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Download: ${doc.label}`}
                >
                  <div className="w-11 h-11 bg-brand-sky/15 rounded-xl flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors flex-shrink-0">
                    <FileText size={20} aria-hidden="true" />
                  </div>
                  <p className="flex-1 font-semibold text-brand-navy text-sm">{doc.label}</p>
                  <Download size={16} className="text-slate-400 group-hover:text-brand-blue transition-colors" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-slate-500 text-center">
            Questions about enrollment? Call us at <a href="tel:3178373307" className="text-brand-blue hover:underline font-medium">(317) 837-3307</a> or email <a href="mailto:gpcdm@indygracepointe.org" className="text-brand-blue hover:underline font-medium">gpcdm@indygracepointe.org</a> to schedule a personal tour.
          </p>
        </div>
      </section>

      {/* What to expect */}
      <section className="section-pad bg-brand-ivory">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionHeader eyebrow="What to Expect" headline="Your Child's First Days" centered={false} />
              <div className="space-y-4">
                {[
                  { emoji: '🤝', title: 'A Warm Welcome', body: 'Our staff will greet you and your child with open arms and help ease any first-day jitters.' },
                  { emoji: '📋', title: 'Orientation Meeting', body: 'We\'ll walk you through our daily routines, policies, and answer every question you have.' },
                  { emoji: '📱', title: 'Ongoing Communication', body: 'We use Procare to keep you connected — daily updates, photos, and progress notes.' },
                  { emoji: '💛', title: 'Transition Support', body: 'We know change can be hard. We\'ll work with you to make your child\'s transition as smooth as possible.' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <span className="text-2xl" aria-hidden="true">{item.emoji}</span>
                    <div>
                      <p className="font-semibold text-brand-navy mb-0.5">{item.title}</p>
                      <p className="text-slate-500 text-sm leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img src="/images/IMG_7459.jpg" alt="Children's first days at Grace Pointe" className="rounded-xl3 shadow-lift w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Parent portal note */}
      <section className="bg-brand-navy section-pad">
        <div className="section-container max-w-2xl text-center">
          <p className="text-brand-amber text-xs font-round font-bold tracking-[0.15em] uppercase mb-3">Already Enrolled?</p>
          <h2 className="text-3xl font-display font-bold text-white mb-4">Access the Parent Portal</h2>
          <p className="text-slate-200 leading-relaxed mb-6">
            Current Grace Pointe families can log in to Procare to view daily reports, manage payments, and stay connected with our staff.
          </p>
          <a
            href={SITE.procare.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold px-8 py-4 text-base"
          >
            Open Procare Portal
            <ExternalLink size={16} aria-hidden="true" />
          </a>
        </div>
      </section>

      <CTASection
        headline="Have Questions? Let's Talk."
        subheadline="Reach out by phone or email—or fill out our contact form and we'll get back to you quickly."
      />
    </main>
  )
}
